<script setup lang="ts">
// [ node_modules ]
import * as $gtag from 'vue-gtag';
// [ vueuse ]
import { useElementVisibility } from '@vueuse/core';
// [ stores ]
import { useAppStore } from '@/stores/app-store';
// [ store - lang ]
import { useLangStore } from '@/stores/lang-store';
import { ja } from '@/locales/about-us/lang-ja';
import { en } from '@/locales/about-us/lang-en';
// [lib]
import { GaFunc } from '@/lib/lib-front';
// -----------------------------------------------------
// [ store - lang ]
const langStore = useLangStore();
langStore.setMessage(en, ja);
const langConfig = ja;
const t = langStore.t;
// -----------------------------------------------------
// [ Nuxt ]
const config = useRuntimeConfig();
const router = useRouter();
const route = useRoute();
// [ stores ]
const appStore = useAppStore();
// -----------------------------------------------------
definePageMeta({
  middleware: 'redirect',
  layout: 'default',
});
useHead({
  meta: [{ name: 'keywords', content: () => t('seo.keywords') }],
});
// プロパティ詳細は以下URL参照すること
// https://github.com/harlan-zw/zhead/blob/main/src/metaFlat.ts
useSeoMeta({
  title: () => t('seo.title'),
  ogTitle: () => t('seo.title'),
  description: () => t('seo.description'),
  ogDescription: () => t('seo.description'),
  ogImage: config.public.url + 'assets/img/OGP.png',
  twitterCard: 'summary_large_image',
});
// -----------------------------------------------------
const isReady = ref(false);
const target1f = ref(null);
const target2f = ref(null);
const target3f = ref(null);
const target1 = ref(null);
const target2 = ref(null);
const target3 = ref(null);
const target1IsVisible = useElementVisibility(target1);
const target2IsVisible = useElementVisibility(target2);
const target3IsVisible = useElementVisibility(target3);
const scrollState = computed(() => {
  if (isReady.value === false) return -1;
  if (target1IsVisible.value === true && target2IsVisible.value === false) {
    return 0;
  }
  if (target1IsVisible.value === true && target2IsVisible.value === true) {
    return 0.5;
  }
  if (target1IsVisible.value === false && target2IsVisible.value === true && target3IsVisible.value === false) {
    return 1;
  }
  if (target1IsVisible.value === false && target2IsVisible.value === true && target3IsVisible.value === true) {
    return 1.5;
  }
  return 2;
});
watch(scrollState, (value) => {
  const index = scrollState.value;
  if (index === 0) {
    setTimeout(() => {
      if (scrollState.value === index) router.replace({ hash: '#asbout-us-row1' });
    }, 100);
  } else if (index === 1) {
    setTimeout(() => {
      if (scrollState.value === index) router.replace({ hash: '#asbout-us-row2' });
    }, 100);
  } else if (index === 2) {
    setTimeout(() => {
      if (scrollState.value === index) router.replace({ hash: '#asbout-us-row3' });
    }, 100);
  }
});

const fixedGroupStype = computed(() => {
  return {
    'min-height': appStore.windowFixedH,
    'min-width': appStore.windowFixedW,
  };
});

const scrollToTop = (elm: any) => {
  // console.log('scrollToTop', elm);
  elm.scrollIntoView({
    behavior: 'smooth',
  });
};
onMounted(async () => {
  const hash = route.hash;
  await nextTick();
  // console.log(hash, target3f);
  if (hash === '#asbout-us-row2') {
    setTimeout(() => {
      (target2f.value as any).scrollIntoView({
        behavior: 'smooth',
      });
      setTimeout(() => {
        isReady.value = true;
      }, 300);
    }, 100);
  } else if (hash === '#asbout-us-row3') {
    setTimeout(() => {
      (target3f.value as any).scrollIntoView({
        behavior: 'smooth',
      });
      setTimeout(() => {
        isReady.value = true;
      }, 300);
    }, 100);
  } else {
    isReady.value = true;
  }
});

const clickNavigateContact = () => {
  $gtag.event(GaFunc.navigate, {
    from: route.path,
    to: `/${langStore.state.lang}/contact-us`,
    name: 'clickNavigateContact',
  });
  router.push(`/${langStore.state.lang}/contact-us`);
};

// ---------------------------------------
</script>
<template>
  <div class="">
    <div class="page-index">
      <div v-if="scrollState !== -1" class="pointer" :style="{ '--top': scrollState }"></div>
      <v-btn class="label" variant="plain" style="text-transform: none" rounded="0" @click="scrollToTop(target1f)">
        {{ t('row1.title') }}
      </v-btn>
      <v-btn class="label" variant="plain" style="text-transform: none" rounded="0" @click="scrollToTop(target2f)">
        {{ t('row2.title') }}
      </v-btn>
      <v-btn class="label" variant="plain" style="text-transform: none" rounded="0" @click="scrollToTop(target3f)">
        {{ t('row3.title') }}
      </v-btn>
    </div>
    <!-- Professional Guild -->
    <div id="asbout-us-row1" ref="target1f" class="fixed-group bg-dark" :style="fixedGroupStype">
      <div ref="target1" class="sokutei"></div>
      <div class="fixed-innner">
        <div class="fixed-sp-fig">
          <img src="/assets/img/about-us/about-us-1.svg" alt="" />
        </div>
        <div class="fixed-title">
          <div class="title f-en-bold">{{ t('row1.title') }}</div>
        </div>
        <div class="fixed-body">
          <div class="message">
            <template v-for="(key, index) in Object.keys(langConfig.row1)" :key="index">
              <div v-if="key !== 'title' && t('row1.' + key)">{{ t('row1.' + key) }}</div>
            </template>
          </div>
          <div class="pc-fig">
            <img src="/assets/img/about-us/about-us-1.svg" alt="" />
          </div>
        </div>
      </div>
      <div class="fixed-scroll-down" :class="{ isShow: !target2IsVisible }">
        <i class="fa-solid fa-angles-down" title="Scroll"></i>
      </div>
    </div>
    <!-- Decentralized Autonomous Organization -->
    <div id="asbout-us-row2" ref="target2f" class="fixed-group bg-light" :style="fixedGroupStype">
      <div ref="target2" class="sokutei"></div>
      <div class="fixed-innner">
        <div class="fixed-sp-fig">
          <img src="/assets/img/about-us/about-us-2.svg" alt="" />
        </div>
        <div class="fixed-title">
          <div class="title f-en-bold">{{ t('row2.title') }}</div>
        </div>
        <div class="fixed-body">
          <div class="message">
            <template v-for="(key, index) in Object.keys(langConfig.row1)" :key="index">
              <div v-if="key !== 'title'">{{ t('row2.' + key) }}</div>
            </template>
          </div>
          <div class="pc-fig">
            <img src="/assets/img/about-us/about-us-2.svg" alt="" />
          </div>
        </div>
      </div>
      <div class="fixed-scroll-down" :class="{ isShow: !target3IsVisible }">
        <i class="fa-solid fa-angles-down" title="Scroll"></i>
      </div>
    </div>
    <!-- INSPIRE LLC -->
    <div id="asbout-us-row3" ref="target3f" class="fixed-group bg-dark" :style="fixedGroupStype">
      <div ref="target3" class="sokutei"></div>
      <div class="fixed-innner">
        <div class="fixed-title">
          <div class="title f-en-bold">{{ t('row3.title') }}</div>
        </div>
        <div class="fixed-body">
          <div class="company">
            <div class="">
              <div>{{ t('row3.left.CompanyName') }}</div>
              <div>{{ t('row3.right.CompanyName') }}</div>
            </div>
            <div class="">
              <div>{{ t('row3.left.HeadOffice') }}</div>
              <div>{{ t('row3.right.HeadOffice') }}</div>
            </div>
            <div class="">
              <div>{{ t('row3.left.YearEstablished') }}</div>
              <div>{{ t('row3.right.YearEstablished') }}</div>
            </div>
            <div class="">
              <div>{{ t('row3.left.Ceo') }}</div>
              <div>{{ t('row3.right.Ceo') }}</div>
            </div>
            <div class="">
              <div>{{ t('row3.left.EMail') }}</div>
              <div>{{ t('row3.right.EMail') }}</div>
            </div>
            <div class="">
              <div>{{ t('row3.left.OpenHours') }}</div>
              <div>{{ t('row3.right.OpenHours') }}</div>
            </div>
            <div class="">
              <div>{{ t('row3.left.HolidaySat') }}</div>
              <div>{{ t('row3.right.HolidaySat') }}</div>
            </div>
            <div v-if="langStore.state.lang === 'en'" class="">
              <div>{{ t('row3.left.Business') }}</div>
              <div>{{ t('row3.right.Business') }}</div>
            </div>
            <div v-if="langStore.state.lang === 'ja'" class="">
              <div>古物商許可番号</div>
              <div>東京都公安委員会 第307760908924号</div>
            </div>
            <div v-if="langStore.state.lang === 'ja'" class="">
              <div>認定査定</div>
              <div class="d-flex flex-wrap">
                <div class="">(財)日本自動車査定協会</div>
                <div class="ps-2">認定査定業務実施店</div>
              </div>
            </div>
            <div v-if="langStore.state.lang === 'ja'" class="">
              <div>事業内容</div>
              <div>
                <ul>
                  <li>自動車の輸出入業務</li>
                  <li>自動車の国内販売業務</li>
                  <li>その他自動車に関する業務</li>
                </ul>
              </div>
            </div>
            <div v-if="langStore.state.lang === 'ja'" class="">
              <div>主要取引先</div>
              <div>
                <ul>
                  <li>株式会社ユー・エス・エス</li>
                  <li>株式会社トヨタユーゼック</li>
                  <li>株式会社シーエーエー</li>
                  <li>株式会社アイオーク</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-4">
        <v-btn class="label" variant="flat" rounded="0" @click="clickNavigateContact()"> Contact US </v-btn>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.dev::after {
  content: '';
  position: absolute;
  inset: 0 0 0 0;
  border: solid 3px green;
}
.bg-dark {
  background-color: rgb(40, 40, 40);
  color: rgb(223, 223, 223);
}
.bg-light {
  background-color: rgb(238, 238, 238);
  color: rgb(24, 24, 24);
}

.page-index {
  position: fixed;
  color: white;
  top: 20px;
  left: 80px;
  z-index: 1;
  font-size: 16px;
  padding-left: 20px;
  mix-blend-mode: difference;
  background-color: rgba(128, 128, 128, 0.393);
  @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
    display: none;
  }

  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &:before {
    content: '';
    position: absolute;
    inset: 6px auto 6px 2px;
    width: 5px;
    border-radius: 5px;
    background-color: rgba(135, 135, 135, 0.481);
    mix-blend-mode: normal;
  }

  .pointer {
    position: absolute;
    line-height: 1em;
    left: 0;
    height: 20px;
    width: 16px;
    transform: translateY(calc(var(--top) * 20px));
    font-size: 16px;
    &:before {
      position: absolute;
      left: 0;
      width: 10px;
      height: 20px;

      font-size: 20px;
      display: flex;
      justify-content: center;
      align-items: center;
      color: white;
      content: '\f0da';
      font-family: 'Font Awesome 6 Free';
      font-weight: 900;
    }
  }
  > .label {
    display: block;
    height: 20px;
    line-height: 20px;
    mix-blend-mode: difference;
    width: auto;
    cursor: pointer;
    &:hover::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1px;
      background-color: white;
    }
  }
}
.fixed-group {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  font-size: 16px;
  @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
    padding: 50px 12px 50px 40px;
  }
  @media screen and (min-width: #{ 600 }px) {
    padding: 20px 0;
  }
  .sokutei {
    position: absolute;
    inset: 20px auto 20px auto;
    width: 1px;
    opacity: 0;
    background-color: green;
    user-select: none;
    pointer-events: none;
  }

  .fixed-innner {
    background-color: rgba(43, 50, 104, 0.326);

    border: solid 1px gray;
    @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
      width: 100%;
      padding: 10px;
    }

    @media screen and (min-width: #{ 600 }px) {
      width: 70%;
      padding: 20px;
    }
  }

  .fixed-scroll-down {
    position: absolute;
    mix-blend-mode: difference;
    height: 30px;
    color: rgba(255, 255, 255, 0.584);
    display: flex;
    justify-content: center;
    align-items: center;
    inset: auto 0 0 0;
    i {
      opacity: 0;
      transform: scaleX(200%);
    }
    &.isShow {
      i {
        transform: scaleX(200%);
        // animation: piku-piku 1500ms linear;
        animation-name: piku-piku;
        animation-timing-function: ease;
        animation-delay: 0ms;
        animation-duration: 1500ms;
        // animation-direction: alternate;
        animation-iteration-count: infinite;
      }
    }
  }

  .fixed-sp-fig {
    @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
      display: block;
    }

    @media screen and (min-width: #{ 600 }px) {
      display: none;
    }
  }

  .fixed-title {
    font-weight: bold;
    @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
      font-size: 18px;
    }
    @media screen and (min-width: #{ 600 }px) {
      font-size: 26px;
    }
  }
  .fixed-body {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    // font-size: 1.6em;

    @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
      // font-size: 1.2em;
      flex-direction: column-reverse;
      justify-content: flex-start;
      align-items: stretch;
    }

    .message {
      flex: 1 1 auto;
      // padding: 0 0 0 8px;

      @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
        font-size: 14px;
        padding: 0 0 0 8px;
        &::before {
          content: '';
          position: absolute;
          inset: 2px auto 2px 0px;
          width: 2px;
          background-color: rgba(255, 255, 255, 0.398);
        }
      }
      @media screen and (min-width: #{ 600 }px) {
        font-size: 24px;
        padding: 0 8px 0 0;
      }
    }

    .pc-fig {
      width: 40%;
      flex: 0 0 40%;
      aspect-ratio: 6 / 4;

      @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
        display: block;
        display: none;
        width: 0%;
        flex: 0 0 0%;
      }

      @media screen and (min-width: #{ 600 }px) {
        display: block;
        width: 40%;
      }

      > img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
      }
    }
    .company {
      width: 100%;
      flex: 1 1 auto;
      font-size: 16px;
      > div {
        display: flex;

        @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
          flex-direction: column;
        }

        width: 100%;
        border-top: solid 1px #80808070;
        line-height: 1.4em;

        > div {
          &:nth-child(1) {
            color: rgb(215, 231, 255);
            width: 30%;
            text-align: right;
            padding: 10px 16px 10px 6px;
            @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
              width: 100%;
              text-align: left;
              padding: 4px 2px;
            }
          }
          &:nth-child(2) {
            width: 70%;
            padding: 10px 6px 10px 16px;
            @media screen and (min-width: #{ 0 }px) and (max-width: #{ 600 - 0.1}px) {
              width: 100%;
              padding: 0 0px 0 0.5em;
            }
            ul {
              padding-left: 1em;
            }
          }
        }
      }
    }
  }
}
</style>
